// skipcq: JS-W1028
import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DashboardLayout from '../../components/layout/DashboardLayout';
import useBeerIngestionReviewKeyboard from '../../hooks/useBeerIngestionReviewKeyboard';
import { listIngestionReviews } from '../../services/beerIngestionService';
import { getBeerIngestionRun } from '../../services/breweryBeerSourcesService';
import {
  captureBeerIngestionReviewAutoAdvance,
  captureBeerIngestionReviewBack,
  captureBeerIngestionReviewError,
  captureBeerIngestionReviewNext,
  captureBeerIngestionReviewPrevious,
} from '../../utils/beerIngestionAnalytics';
import {
  breweryNamesFromQueueRows,
  buildBeerIngestionInboxPath,
  buildBeerIngestionReviewPath,
  mergeStashedBreweryName,
  nextQueuedRunIdAfter,
  queueRunIdsFromRows,
  resolveQueueNeighbors,
  resolveReviewBreweryName,
} from '../../utils/brewery/beerIngestionReviewQueue';
import BeerIngestionRunDetailBody, {
  RunTechnicalIdsPopover,
} from '../brewery/BeerIngestionRunDetailBody';

const PAGE_CONTENT_SX = {
  mt: 2,
  mb: 4,
  px: { xs: 2, sm: 3 },
};

const HEADER_ROW_SX = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 1,
  mb: 1,
};

const QUEUE_NAV_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  flexWrap: 'wrap',
};

function ReviewPageHeader({
  showQueueNav,
  previousDisabled,
  nextDisabled,
  positionLabel,
  onBack,
  onPrevious,
  onNext,
  run,
}) {
  return (
    <Box sx={{ mb: 2 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mb: 1 }}>
        Back to review inbox
      </Button>
      <Box sx={HEADER_ROW_SX}>
        <Typography variant="h6" component="h1">
          Ingestion run
        </Typography>
        {run ? <RunTechnicalIdsPopover run={run} /> : null}
      </Box>
      {showQueueNav ? (
        <Box sx={QUEUE_NAV_SX}>
          <Button
            startIcon={<ArrowBackIosIcon />}
            onClick={onPrevious}
            disabled={previousDisabled}
          >
            Previous
          </Button>
          {positionLabel ? (
            <Typography variant="body2" color="text.secondary">
              {positionLabel}
            </Typography>
          ) : null}
          <Button
            endIcon={<ArrowForwardIcon />}
            onClick={onNext}
            disabled={nextDisabled}
          >
            Next
          </Button>
        </Box>
      ) : null}
    </Box>
  );
}

ReviewPageHeader.propTypes = {
  showQueueNav: PropTypes.bool,
  previousDisabled: PropTypes.bool,
  nextDisabled: PropTypes.bool,
  positionLabel: PropTypes.string,
  onBack: PropTypes.func.isRequired,
  onPrevious: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  run: PropTypes.object,
};

export default function BeerIngestionReviewPage() {
  const { runId } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const stateCode = searchParams.get('state') || '';
  const locationQueueIds = location.state?.queueRunIds;
  const locationBreweryNames = location.state?.breweryNames;

  const [run, setRun] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [review, setReview] = useState(null);
  const [queueRows, setQueueRows] = useState([]);
  const [queueRunIds, setQueueRunIds] = useState(
    () => (Array.isArray(locationQueueIds) ? locationQueueIds : []),
  );
  const [stashedBreweryNames, setStashedBreweryNames] = useState(
    () => (locationBreweryNames && typeof locationBreweryNames === 'object'
      ? { ...locationBreweryNames }
      : {}),
  );
  const [detailBreweryName, setDetailBreweryName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function loadRun() {
      setLoading(true);
      setError(null);
      try {
        const detail = await getBeerIngestionRun(runId);
        if (cancelled) {
          return;
        }
        if (!detail?.run) {
          setRun(null);
          setSnapshot(null);
          setComparison(null);
          setReview(null);
          setDetailBreweryName('');
          setError('Ingestion run not found');
          return;
        }
        setRun(detail.run);
        setSnapshot(detail.snapshot);
        setComparison(detail.comparison);
        setReview(detail.review);
        const nameFromDetail = detail.run?.breweryName || detail.breweryName || '';
        setDetailBreweryName(nameFromDetail);
        if (nameFromDetail) {
          setStashedBreweryNames((current) => mergeStashedBreweryName(
            current,
            runId,
            nameFromDetail,
          ));
        }
      } catch (loadError) {
        if (!cancelled) {
          setError(loadError?.message || 'Failed to load review workspace');
          captureBeerIngestionReviewError(runId, '');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }
    loadRun();
    return () => {
      cancelled = true;
    };
  }, [runId]);

  useEffect(() => {
    if (!stateCode) {
      return undefined;
    }
    let cancelled = false;
    async function loadQueue() {
      try {
        const listed = await listIngestionReviews(stateCode);
        if (cancelled) {
          return;
        }
        setQueueRows(listed.rows);
        setQueueRunIds(queueRunIdsFromRows(listed.rows));
        setStashedBreweryNames((current) => {
          let next = current;
          listed.rows.forEach((row) => {
            next = mergeStashedBreweryName(next, row.runId, row.breweryName);
          });
          return next;
        });
      } catch {
        if (!cancelled && !Array.isArray(locationQueueIds)) {
          setQueueRunIds([]);
        }
      }
    }
    loadQueue();
    return () => {
      cancelled = true;
    };
  }, [locationQueueIds, stateCode]);

  const neighbors = useMemo(
    () => resolveQueueNeighbors(runId, queueRunIds),
    [queueRunIds, runId],
  );
  const showQueueNav = Boolean(stateCode);
  const breweryName = resolveReviewBreweryName({
    runId,
    runBreweryName: run?.breweryName,
    detailBreweryName,
    stashedNames: stashedBreweryNames,
    queueRows,
    locationBreweryNames,
  });

  const handleBack = useCallback(() => {
    captureBeerIngestionReviewBack(runId, run?.breweryId);
    navigate(buildBeerIngestionInboxPath());
  }, [navigate, run, runId]);

  const handlePrevious = useCallback(() => {
    if (!neighbors.previousRunId) {
      return;
    }
    captureBeerIngestionReviewPrevious(runId, run?.breweryId);
    navigate(buildBeerIngestionReviewPath(neighbors.previousRunId, stateCode), {
      state: {
        queueRunIds,
        breweryNames: locationBreweryNames || breweryNamesFromQueueRows(queueRows),
      },
    });
  }, [
    locationBreweryNames,
    navigate,
    neighbors.previousRunId,
    queueRows,
    queueRunIds,
    run,
    runId,
    stateCode,
  ]);

  const handleNext = useCallback(() => {
    if (!neighbors.nextRunId) {
      return;
    }
    captureBeerIngestionReviewNext(runId, run?.breweryId);
    navigate(buildBeerIngestionReviewPath(neighbors.nextRunId, stateCode), {
      state: {
        queueRunIds,
        breweryNames: locationBreweryNames || breweryNamesFromQueueRows(queueRows),
      },
    });
  }, [
    locationBreweryNames,
    navigate,
    neighbors.nextRunId,
    queueRows,
    queueRunIds,
    run,
    runId,
    stateCode,
  ]);

  const handleReviewUpdated = useCallback((nextRun, nextReview) => {
    if (nextRun) {
      setRun(nextRun);
    }
    if (nextReview) {
      setReview(nextReview);
    }
  }, []);

  const handleApplySuccess = useCallback(async () => {
    captureBeerIngestionReviewAutoAdvance(runId, run?.breweryId);
    if (!stateCode) {
      navigate(buildBeerIngestionInboxPath());
      return;
    }
    try {
      const listed = await listIngestionReviews(stateCode);
      const refreshedIds = queueRunIdsFromRows(listed.rows);
      const nextRunId = nextQueuedRunIdAfter(runId, queueRunIds, refreshedIds);
      if (!nextRunId) {
        navigate(buildBeerIngestionInboxPath());
        return;
      }
      navigate(buildBeerIngestionReviewPath(nextRunId, stateCode), {
        state: {
          queueRunIds: refreshedIds,
          breweryNames: breweryNamesFromQueueRows(listed.rows),
        },
      });
    } catch {
      navigate(buildBeerIngestionInboxPath());
    }
  }, [navigate, queueRunIds, run, runId, stateCode]);

  useBeerIngestionReviewKeyboard({
    enabled: showQueueNav,
    onPrevious: handlePrevious,
    onNext: handleNext,
  });

  return (
    <DashboardLayout title="Beer ingestion">
      <Box sx={PAGE_CONTENT_SX}>
        <ReviewPageHeader
          showQueueNav={showQueueNav}
          previousDisabled={!neighbors.previousRunId}
          nextDisabled={!neighbors.nextRunId}
          positionLabel={neighbors.positionLabel}
          onBack={handleBack}
          onPrevious={handlePrevious}
          onNext={handleNext}
          run={run}
        />
        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} aria-label="Loading ingestion run" />
          </Box>
        ) : null}
        {!loading && run ? (
          <BeerIngestionRunDetailBody
            key={run.id}
            run={run}
            snapshot={snapshot}
            comparison={comparison}
            review={review}
            breweryId={run.breweryId || ''}
            breweryName={breweryName}
            openBreweryInNewTab
            onReviewUpdated={handleReviewUpdated}
            onApplySuccess={handleApplySuccess}
          />
        ) : null}
      </Box>
    </DashboardLayout>
  );
}
